// Small shared UI primitives: spinner, empty/error states, segmented control,
// dropdown scaffolding. Styled by the classes in index.css.

import type { ReactElement, ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from './Icon';

export function Spinner(): ReactElement {
  return (
    <div className="center">
      <div className="spinner" aria-label="Loading" />
    </div>
  );
}

export function StateCard({
  icon = 'alert-triangle',
  title,
  body,
  action,
}: {
  icon?: IconName;
  title: string;
  body?: string;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="card state-card">
      <span className="state-icon">
        <Icon name={icon} size={26} />
      </span>
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

// Toggle-button group (not tabs — there is no tabpanel; aria-pressed keeps
// the semantics honest and every option natively keyboard-reachable).
export function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string; ariaLabel?: string }[];
  value: T;
  onChange: (v: T) => void;
}): ReactElement {
  return (
    <div className="segmented" role="group">
      {options.map((o) => (
        <button
          key={o.value}
          aria-pressed={o.value === value}
          aria-label={o.ariaLabel}
          className={`seg${o.value === value ? ' active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Toggle switch (styled checkbox — .switch in index.css). The label names it
// for screen readers; the visible label sits next to it in the caller.
export function Switch({
  checked,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel?: string;
}): ReactElement {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="track" />
    </label>
  );
}

// Close-on-outside-click / Escape for dropdown menus.
export function useDismiss(ref: RefObject<HTMLElement | null>, onDismiss: () => void): void {
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onDismiss();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, onDismiss]);
}

export function Dropdown({
  trigger,
  children,
  align = 'left',
  closeOnClick = true,
}: {
  /** Render prop for the trigger button; receives open state. */
  trigger: (open: boolean) => ReactNode;
  /** Menu contents; rendered only while open. Clicking an item closes via
   *  the onClick bubbling to the wrapper. */
  children: ReactNode;
  align?: 'left' | 'right';
  /** Off for menus holding controls (toggles) that shouldn't dismiss it. */
  closeOnClick?: boolean;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(ref, () => setOpen(false));

  return (
    <div className="dropdown" ref={ref}>
      <span onClick={() => setOpen((o) => !o)}>{trigger(open)}</span>
      {open && (
        <div
          className={`menu${align === 'right' ? ' menu-right' : ''}`}
          onClick={closeOnClick ? () => setOpen(false) : undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}

// Modal dialog — centered sheet over a dimmed backdrop. Closes on backdrop
// click, Escape, or the × button. `wide` is for sheets holding two columns of
// form fields or wrapped config text (the share dialog).
export function Modal({
  title,
  onClose,
  wide = false,
  children,
}: {
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: ReactNode;
}): ReactElement {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal${wide ? ' modal-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3>{title}</h3>
          <button
            className="icon-btn"
            onClick={onClose}
            data-tip={t('common.close')}
            aria-label={t('common.close')}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// Side drawer — full-height panel sliding in from the right over a dimmed
// backdrop. Same dismissal as Modal (backdrop click, Escape); focus moves into
// the panel on open and back to whatever opened it on close. Callers lay out
// the inside with .drawer-head (fixed) and .drawer-body (scrolls).
export function Drawer({
  label,
  onClose,
  children,
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
}): ReactElement {
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => opener?.focus();
  }, []);

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <aside
        ref={panelRef}
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </aside>
    </div>
  );
}

// Minimal toast — one message at a time, auto-dismisses.
let pushToast: ((msg: string) => void) | null = null;

export function toast(msg: string): void {
  pushToast?.(msg);
}

export function ToastHost(): ReactElement | null {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    pushToast = (m: string) => {
      setMsg(m);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setMsg(null), 3500);
    };
    return () => {
      pushToast = null;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (!msg) return null;
  return (
    <div className="toast" role="status">
      <Icon name="check-circle" size={18} />
      {msg}
    </div>
  );
}
